// PanoStrip — the pannable panorama strip shared by the pathfinder's viewer and
// the authoring tool's calibrator.
//
// Two things a plain overflow scroller can't do:
//
//  · WRAP. A 360° photo's left and right edges are the same wall, so a scroller
//    hits a hard stop mid-corridor and you have to drag all the way back to see
//    what was one step the other way. This tiles the image and tracks its own
//    offset modulo one image width, so turning past the seam continues into the
//    same picture — stitched, like really turning on the spot. A flat photo is
//    not continuous, so `wrap={false}` clamps and renders a single copy.
//
//  · MOMENTUM. Flick and let go and the view keeps turning, decaying under
//    friction. Optional, because the calibrator wants the photo to stop exactly
//    where it was put.
//
// The offset lives in a ref and is written straight to the track's transform,
// never through React state: a consumer typically re-renders on every reported
// fraction, and routing the offset through state would fight the drag.
import { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';

/** Velocity multiplier per 60fps frame while coasting. */
const FRICTION = 0.94;
/** Below this (px/ms) the coast has visually stopped. */
const STOP_V = 0.02;

const PanoStrip = forwardRef(function PanoStrip({
  src, alt = 'Panorama', wrap = true, momentum = true,
  initialFrac, onFrac, onInteract, onMetrics,
  className = '', imgClassName = '', hostClassName = '', style, children,
}, ref) {
  const hostRef = useRef(null);
  const trackRef = useRef(null);
  const [imgW, setImgW] = useState(0);
  const [hostW, setHostW] = useState(0);
  const onFracRef = useRef(onFrac);
  const onInteractRef = useRef(onInteract);
  const onMetricsRef = useRef(onMetrics);
  const initialFracRef = useRef(initialFrac);
  const wrapRef = useRef(wrap);
  const momentumRef = useRef(momentum);
  useEffect(() => { onFracRef.current = onFrac; }, [onFrac]);
  useEffect(() => { onInteractRef.current = onInteract; }, [onInteract]);
  useEffect(() => { onMetricsRef.current = onMetrics; }, [onMetrics]);
  useEffect(() => { initialFracRef.current = initialFrac; }, [initialFrac]);
  useEffect(() => { wrapRef.current = wrap; }, [wrap]);
  useEffect(() => { momentumRef.current = momentum; }, [momentum]);

  // x = image-space px at the viewport's left edge.
  const st = useRef({ x: 0, v: 0, imgW: 0, w: 0, raf: 0, last: 0, drag: false });

  const apply = () => {
    const s = st.current;
    if (s.imgW > 0) {
      if (wrapRef.current) s.x = ((s.x % s.imgW) + s.imgW) % s.imgW;
      else s.x = Math.max(0, Math.min(Math.max(0, s.imgW - s.w), s.x));
    }
    if (trackRef.current) trackRef.current.style.transform = `translate3d(${-s.x}px,0,0)`;
    if (s.imgW > 0) {
      const f = (s.x + s.w / 2) / s.imgW;
      onFracRef.current?.(wrapRef.current ? ((f % 1) + 1) % 1 : Math.max(0, Math.min(1, f)));
    }
  };

  const coast = (now) => {
    const s = st.current;
    const dt = Math.min(64, s.last ? now - s.last : 16) || 16;
    s.last = now;
    const before = s.x;
    s.x += s.v * dt;
    s.v *= Math.pow(FRICTION, dt / 16.667);
    apply();
    // A non-wrapping strip that has hit its end should stop, not grind there.
    if (!wrapRef.current && Math.abs(s.x - before) < 0.01) { s.v = 0; s.raf = 0; s.last = 0; return; }
    if (Math.abs(s.v) > STOP_V) s.raf = requestAnimationFrame(coast);
    else { s.v = 0; s.raf = 0; s.last = 0; }
  };
  const stopCoast = () => {
    const s = st.current;
    if (s.raf) cancelAnimationFrame(s.raf);
    s.raf = 0; s.v = 0; s.last = 0;
  };

  useImperativeHandle(ref, () => ({
    /** Put image fraction `f` under the centre of the view. */
    setCentreFrac(f) {
      const s = st.current;
      stopCoast();
      if (s.imgW > 0) { s.x = f * s.imgW - s.w / 2; apply(); }
    },
    /** Turn by a fraction of the whole image (positive = look right). */
    nudgeFrac(df) {
      const s = st.current;
      stopCoast();
      if (s.imgW > 0) { s.x += df * s.imgW; apply(); }
    },
    /** The strip's box on screen, for consumers positioning their own overlays. */
    getRect() { return hostRef.current?.getBoundingClientRect() || null; },
    metrics() { return { imgW: st.current.imgW, hostW: st.current.w }; },
  }), []);

  // One copy is as wide as the image's aspect ratio at this height.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !src) { setImgW(0); return; }
    let dead = false, first = true;
    const measure = (natW, natH) => {
      if (dead || !natH) return;
      const h = host.clientHeight;
      if (!h) return;
      const w = Math.max(1, Math.round(h * (natW / natH)));
      st.current.imgW = w; st.current.w = host.clientWidth;
      setImgW(w); setHostW(host.clientWidth);
      onMetricsRef.current?.({ imgW: w, hostW: host.clientWidth });
      // Land on the caller's opening view the first time this image sizes.
      if (first) {
        first = false;
        const f = initialFracRef.current;
        if (typeof f === 'number') { st.current.v = 0; st.current.x = f * w - st.current.w / 2; }
      }
      apply();
    };
    const probe = new Image();
    probe.onload = () => measure(probe.naturalWidth, probe.naturalHeight);
    probe.src = src;
    const ro = new ResizeObserver(() => {
      if (probe.naturalHeight) measure(probe.naturalWidth, probe.naturalHeight);
    });
    ro.observe(host);
    return () => { dead = true; ro.disconnect(); };
  }, [src]);

  // Enough copies to cover the viewport from any offset in [0, imgW).
  const copies = wrap && imgW > 0 ? 1 + Math.ceil(hostW / imgW) : 1;

  // Pointer drag + flick. Velocity is an EMA of px/ms so one jittery frame at
  // release doesn't decide the whole throw.
  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    let lastX = 0, lastT = 0;

    const down = (e) => {
      if (e.button != null && e.button !== 0) return;
      stopCoast();
      st.current.drag = true;
      onInteractRef.current?.('down');
      lastX = e.clientX; lastT = e.timeStamp;
      host.setPointerCapture?.(e.pointerId);
      host.classList.add('ps-dragging');
    };
    const move = (e) => {
      const s = st.current;
      if (!s.drag) return;
      const dx = e.clientX - lastX;
      const dt = Math.max(1, e.timeStamp - lastT);
      lastX = e.clientX; lastT = e.timeStamp;
      s.x -= dx;
      s.v = 0.75 * (-dx / dt) + 0.25 * s.v;
      apply();
    };
    const up = () => {
      const s = st.current;
      if (!s.drag) return;
      s.drag = false;
      host.classList.remove('ps-dragging');
      onInteractRef.current?.('up');
      if (momentumRef.current && Math.abs(s.v) > STOP_V) { s.last = 0; s.raf = requestAnimationFrame(coast); }
      else s.v = 0;
    };
    // Trackpads send horizontal deltas; a mouse wheel only vertical ones.
    const wheel = (e) => {
      const s = st.current;
      const d = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.deltaY;
      if (!d) return;
      e.preventDefault();
      stopCoast();
      onInteractRef.current?.('wheel');
      s.x += d;
      apply();
    };

    host.addEventListener('pointerdown', down);
    host.addEventListener('pointermove', move);
    host.addEventListener('pointerup', up);
    host.addEventListener('pointercancel', up);
    host.addEventListener('wheel', wheel, { passive: false });
    return () => {
      host.removeEventListener('pointerdown', down);
      host.removeEventListener('pointermove', move);
      host.removeEventListener('pointerup', up);
      host.removeEventListener('pointercancel', up);
      host.removeEventListener('wheel', wheel);
      stopCoast();
    };
    // Attached once, deliberately: re-binding native listeners on every render
    // would drop an in-flight drag. `coast`/`stopCoast`/`apply` only ever touch
    // refs, so the closure staleness this rule guards against cannot arise.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={`ps-wrap ${className}`} style={style}>
      <div className={`ps-host ${hostClassName}`} ref={hostRef}>
        {src ? (
          <div className="ps-track" ref={trackRef}>
            {Array.from({ length: copies }, (_, i) => (
              <img key={i} className={imgClassName} src={src} alt={i === 0 ? alt : ''} draggable={false}
                   style={{ left: `${i * imgW}px`, width: imgW ? `${imgW}px` : 'auto' }} />
            ))}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
});

export default PanoStrip;
